import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { SESSION_TIMED_OUT, USER_TOKEN_NAME, useAuthToken } from './useAuthToken';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { useLessonFeedbackStatusStorage } from './useLessonFeedbackStatusStorage';
import { getOrCreateSessionId, getAndClearSessionUserId, setSessionUserId } from '../utils/sentryTrackingConfig';
import { beginLogout, endLogout, getLogoutEventsSent, setLogoutEventsSent, resetLogoutTracking } from '../utils/logoutTrackingGuard';
import { logger } from '../utils/logger';
import { loginWithIdpV2, logoutFromIdpV2, persistIdpV2Tokens } from '../utils/idpV2Auth';

export type LoginResponse = {
  token?: string;
  uri?: string;
  sid?: string;
  verified?: string;
  email?: string;
  id?: string;
};

type LoginMutationResult = {
  loading: boolean;
  error?: Error;
};

type UseLoginMutationResponse = [(email: string, password: string) => void, () => void, LoginMutationResult];
export const useLoginMutation = (isParentApp?: boolean): UseLoginMutationResponse => {
  const { setAuthToken, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeAuthPassword, removeVisitProfile, removeParentAuthToken, removeFirstResetLesson } = useAuthToken();
  const { removeFeedbackStatus } = useLessonFeedbackStatusStorage();
  const client = useApolloClient();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();

  const { identify, trackEvent, segment } = useAnalytics();
  const [mutationResults, setMutationResults] = useState<LoginMutationResult>({ loading: false });

  const finishLogin = useCallback((result: { access_token: string; session_id?: string | null; refresh_token?: string | null; access_expire_date?: string | null; refresh_expire_date?: string | null }, email?: string | null, id?: string | null) => {
    resetLogoutTracking(); // allow logout events on next logout
    removeAuthToken();
    removeFeedbackStatus();
    persistIdpV2Tokens(result);
    setAuthToken(result.access_token, true);

    const sessionTimedOut = localStorage.getItem(SESSION_TIMED_OUT);
    if (sessionTimedOut) {
      logger.debug('[Login] Clearing session timeout flag', {
        timeoutSetAt: new Date(Number(sessionTimedOut)).toISOString(),
        timeElapsed: Date.now() - Number(sessionTimedOut),
        timestamp: new Date().toISOString()
      });
    }
    localStorage.removeItem(SESSION_TIMED_OUT);
    if (id) {
      segment?.identify(id);
      identify(id, {
        email,
      });
    }
    const sessionId = getOrCreateSessionId();
    const timestamp = new Date().toISOString();
    const d = { email, loginType: 'Email', userId: id, sessionId, timestamp };
    trackEvent(Events.actions.logIn, d);
    trackEvent(Events.actions.sessionStarted, d);
    if (id) setSessionUserId(id);
    if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {
      const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
      push(sharePath);
    } else {
      push('/');
    }
  }, [identify, push, removeAuthToken, removeFeedbackStatus, returnUrl, search, segment, setAuthToken, trackEvent]);

  const login = useCallback((email: string, password: string) => {
    setMutationResults({ loading: true });
    loginWithIdpV2(email, password)
      .then((result) => {
        if (result.access_token) {
          finishLogin({ ...result, access_token: result.access_token }, email, result.user_id);
        } else if (result.mfa_required && result.challenge_id && (result.method === 'totp' || result.method === 'totp_setup')) {
          const state: any = { idpChallengeId: result.challenge_id, idpMethod: result.method, email }
          if (result.method === 'totp_setup' && result.uri) {
            state.totpUri = result.uri
          }
          push('/verify', state);
        } else if (result.mfa_required && result.challenge_id && result.method === 'email_otp') {
          push('/admin/verify', { idpChallengeId: result.challenge_id, idpMethod: result.method, email: result.email || email });
        } else {
          throw new Error('Login attempt failed');
        }
        setMutationResults({ loading: false });
      })
      .catch((error) => {
        trackEvent(Events.actions.logInFailed, {email, loginType: 'Email', clientId: localStorage.getItem('clientId'), message: "Login attempt failed", error: error.message});
        logger.debug(`[Error]`, error);
        setMutationResults({ loading: false, error });
      });
  }, [finishLogin, push, trackEvent]);

  const logout = useCallback(async () => {
    if (!beginLogout()) return;
    const withTimeout = async (p: Promise<unknown>, ms = 1200) => {
      await Promise.race([p, new Promise<void>((resolve) => setTimeout(resolve, ms))]);
    };

    // Only send logout events once per session (shared guard – useLoginMutation and useAuthToken.logout may both run)
    if (!getLogoutEventsSent()) {
      setLogoutEventsSent(true);
      const sessionId = getOrCreateSessionId();
      const userId = getAndClearSessionUserId();
      try {
        await withTimeout(trackEvent(Events.actions.sessionEnded, {
          userId: userId ?? undefined,
          sessionId,
          timestamp: new Date().toISOString(),
        }));
        await withTimeout(trackEvent(Events.actions.logOut));
        const segmentWithQueue = segment as { queue?: { flush?: () => Promise<unknown> } } | undefined;
        if (typeof segmentWithQueue?.queue?.flush === 'function') {
          await withTimeout(segmentWithQueue.queue.flush());
        } else {
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (e) {
        logger.error('[Login] Error tracking logout events:', e);
      }
    }

    try {
      await logoutFromIdpV2(localStorage.getItem(USER_TOKEN_NAME));
    } catch (e) {
      logger.error('[Login] IdPv2 logout failed:', e);
    }

    try {
      await client.clearStore();
    } catch (e) {
      logger.error('[Login] clearStore failed during logout:', e);
    }
    removeAuthToken();
    removeHsaAuthToken();
    removeLockAccount();
    removeAuthPassword();
    removeVisitProfile();
    removeParentAuthToken();
    removeFirstResetLesson();
    endLogout();
    if (isParentApp) {
      window.location.href = 'https://openparachuteschools.com/';
    } else {
      window.location.href = '/login';
    }
  }, [client, isParentApp, removeAuthPassword, removeAuthToken, removeHsaAuthToken, removeLockAccount, removeVisitProfile, removeParentAuthToken, trackEvent, removeFirstResetLesson, segment]);

  return [login, logout, mutationResults];
}
